import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from 'App/common/Notification/Notification.config';
import { apiEndpoint, APP_REQUEST_CONFIGS, appChannel } from 'config/track-config';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { AppAsyncThunkOpts } from 'models/Store';
import { clearNotification, createNotification } from 'store/notification';
import { AppDispatch } from 'store/store';
import axiosInstance from 'utils/axios.utils';

import { deleteSession } from '../../customer';

export const deleteShipments = createAsyncThunk<
  string[] | void,
  { trackingIds: string[]; consignmentIds: string[]; nickname: string | null },
  AppAsyncThunkOpts
>('shipment/deleteShipment', async ({ trackingIds, consignmentIds, nickname }, thunkAPI) => {
  try {
    thunkAPI.dispatch(clearNotification());
    const isMultiDelete = trackingIds.length > 1;
    const ids = isMultiDelete ? consignmentIds : trackingIds;

    const response = await apiDeleteShipment(ids.join(','), isMultiDelete);

    if (response.status === 200 || response.status === 202) {
      return consignmentIds;
    }
  } catch (error) {
    const authError = error as AuthError;
    if (
      (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
      (authError.isLoginRequiredError && authError.isLoginRequiredError())
    ) {
      const status = 401;
      handleDeleteShipmentError(thunkAPI.dispatch, trackingIds, status, nickname);
      return thunkAPI.rejectWithValue({
        status,
        data: authError.message,
        isAxiosError: false,
      });
    }

    if (axios.isAxiosError(error)) {
      const axiosErr = error as AxiosError;
      const { data, status } = axiosErr?.response || {};
      handleDeleteShipmentError(thunkAPI.dispatch, trackingIds, status, nickname);
      return thunkAPI.rejectWithValue({
        status,
        data,
        isAxiosError: axiosErr.isAxiosError,
      });
    }
    throw error;
  }
});

/**
 * Makes an API call to delete shipment from Shipments Gateway API
 * @param trackingIds an array of article ids to be removed from authenticated watchlist
 * @param isMultiDelete boolean value to check if the selected is single or multiple articles
 */
const apiDeleteShipment = (trackingIds: string, isMultiDelete: boolean) => {
  const _shipmentUrl = `${apiEndpoint.shipmentsV1}${isMultiDelete ? '/async?ids=' : '?trackingIds='}${trackingIds}`;
  const requestConfig = APP_REQUEST_CONFIGS.shipment;
  const apChannelName = isMultiDelete ? appChannel.multiRemove : appChannel.detail;
  requestConfig.headers.AP_CHANNEL_NAME = apChannelName;
  return axiosInstance.delete(_shipmentUrl, requestConfig);
};

const handleDeleteShipmentError = (
  dispatch: AppDispatch,
  trackingIds: string[],
  errorStatus?: number,
  nickname: string | null = null
) => {
  const notification: Notification = {
    type: NotificationType.ALERT,
    status: NotificationStatus.INFO,
    message: errorContent.deleteError(trackingIds, nickname),
  };
  switch (errorStatus) {
    case 401:
      notification.message = errorContent.expiredSessionOnRemoveArticle;
      dispatch(deleteSession());
      break;
    case 500:
    case 503:
      notification.message = errorContent.deleteServerError(trackingIds, nickname);
      break;
    default:
  }
  dispatch(clearNotification());
  dispatch(createNotification(notification));
};
